import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Boxes, Loader2, RotateCcw, Send, ChevronDown,
    CheckCircle, Navigation, Building2, IndianRupee,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchCCForCCStockClose,
    checkLastCCStockClose,
    fetchCCStockAmount,
    saveCCStockClose,
    clearStockAmount,
    clearSaveResult,
    resetCCStockClose,
    selectCostCentersArray,
    selectCostCentersLoading,
    selectLastClose,
    selectLastCloseLoading,
    selectStockAmount,
    selectStockAmountLoading,
    selectSaveStatus,
    selectSaveLoading,
    selectSaveError,
} from '../../slices/purchaseSlice/ccStockCloseSlice';

// ── Constants ─────────────────────────────────────────────────────────────────

const TRANSACTION_TYPES = ['Add', 'Substract'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

// Parse a date string coming back from the API ("dd-MMM-yyyy", ISO, etc.) into a JS Date
const parseAPIDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    const ddMonYYYY = /^(\d{2})-([A-Za-z]{3})-(\d{4})$/.exec(val);
    if (ddMonYYYY) {
        const [, dd, mon, yyyy] = ddMonYYYY;
        const monthIdx = MONTH_ABBR.findIndex((m) => m.toLowerCase() === mon.toLowerCase());
        if (monthIdx >= 0) return new Date(parseInt(yyyy, 10), monthIdx, parseInt(dd, 10));
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

const inputCls =
    'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-2xl">
        <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 text-indigo-500" />
            <div>
                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>}
            </div>
        </div>
        {action}
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const CCStockClose = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    // ── Redux ──────────────────────────────────────────────────────────────
    const costCenters = useSelector(selectCostCentersArray);
    const ccLoading = useSelector(selectCostCentersLoading);
    const lastClose = useSelector(selectLastClose);
    const lastCloseLoading = useSelector(selectLastCloseLoading);
    const stockAmount = useSelector(selectStockAmount);
    const stockAmountLoading = useSelector(selectStockAmountLoading);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    // ── Form state ────────────────────────────────────────────────────────
    const [costCenter, setCostCenter] = useState('');
    const [toDate, setToDate] = useState(null);
    const [transactionType, setTransactionType] = useState('');
    const [amount, setAmount] = useState('');
    const [remarks, setRemarks] = useState('');

    // ── Init ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (userId) {
            dispatch(fetchCCForCCStockClose({ userId: parseInt(userId, 10) || 0, roleId: parseInt(roleId, 10) || 0 }));
        }
        return () => {
            dispatch(resetCCStockClose());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // On CC change: check last close
    useEffect(() => {
        setToDate(null); setTransactionType(''); setAmount(''); setRemarks('');
        dispatch(clearStockAmount());
        if (costCenter) {
            dispatch(checkLastCCStockClose(costCenter));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costCenter]);

    // Derived "from date" — CC opening date when no prior close exists, else the last close date
    const closeExist = lastClose?.CloseExist;
    const effectiveFromDate = closeExist === 'No' ? lastClose?.CCOpeningDate : lastClose?.FromDate;
    const fromDateLabel = closeExist === 'No' ? 'Opening Date' : 'Last Close Date';
    const fromDateDisplay = closeExist === 'No' ? lastClose?.CCOpeningDate : lastClose?.PrevCloseDate;

    // On To Date change: fetch stock amounts
    useEffect(() => {
        setTransactionType(''); setAmount('');
        dispatch(clearStockAmount());
        if (costCenter && effectiveFromDate && toDate) {
            dispatch(fetchCCStockAmount({
                ccCode: costCenter,
                fromDate: effectiveFromDate,
                toDate: formatDateForAPI(toDate),
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toDate]);

    // Save result effect
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('Details Added Successfully.');
            dispatch(clearSaveResult());
            handleFullReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleFullReset = () => {
        setCostCenter(''); setToDate(null); setTransactionType(''); setAmount(''); setRemarks('');
        dispatch(resetCCStockClose());
        if (userId) {
            dispatch(fetchCCForCCStockClose({ userId: parseInt(userId, 10) || 0, roleId: parseInt(roleId, 10) || 0 }));
        }
    };

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) setAmount(val);
    };

    const lastClosingAmount = lastClose?.LastClosingAmount || 0;
    const currentStockAmount = stockAmount?.CurrentStockAmount || 0;
    const currentValue = useMemo(() => currentStockAmount + lastClosingAmount, [currentStockAmount, lastClosingAmount]);

    const numAmount = parseFloat(amount) || 0;
    const closingAmount = useMemo(() => {
        if (!transactionType || !amount) return null;
        return transactionType === 'Add' ? (currentValue + numAmount) : (currentValue - numAmount);
    }, [transactionType, amount, currentValue, numAmount]);

    const handleSubmit = () => {
        if (!costCenter) { toast.warn('Select Cost/Job Center.'); return; }
        if (!toDate) { toast.warn('Select Date.'); return; }
        if (currentValue < 0) { toast.warn('Select Valid Year.'); return; }
        if (!transactionType) { toast.warn('Select Transaction Type.'); return; }
        if (!amount) { toast.warn('Enter Amount.'); return; }
        if (!remarks.trim()) { toast.warn('Enter Remarks.'); return; }
        if (closingAmount === null || closingAmount < 0) { toast.warn('Invalid Closing Amount.'); return; }

        dispatch(saveCCStockClose({
            ccCode: costCenter,
            fromDate: effectiveFromDate,
            toDate: formatDateForAPI(toDate),
            transactionType,
            amount: numAmount,
            totalPurchaseAmt: stockAmount?.TotalPurchaseAmt || 0,
            newStockAmt: stockAmount?.NewStockAmt || 0,
            oldStockAmt: stockAmount?.OldStockAmt || 0,
            dailyIssueAmt: stockAmount?.DailyIssueAmt || 0,
            remarks,
            currentValue,
            closingAmount,
            lastCloseId: lastClose?.LastCloseId || 0,
            roleId,
            createdBy: userName,
        }));
    };

    // ── Derived ───────────────────────────────────────────────────────────
    const isBusy = saveLoading || ccLoading || lastCloseLoading;
    const selectedCCName = costCenters.find((c) => c.CC_Code === costCenter)?.CC_Name || '—';
    const showFields = !!lastClose;
    const showStockInfo = !!stockAmount;

    const showSummary = costCenter && toDate && transactionType && amount && remarks.trim() &&
        closingAmount !== null && closingAmount >= 0;
    const canSubmit = showSummary && !saveLoading;

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* ── Page Header ──────────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <Boxes className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Purchase Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'Update Closing Stock'}
                                </h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Close the cost center stock value for a period</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">Purchase / CC Stock Close</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Section 1: Cost Center ─────────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader
                        icon={Building2}
                        title="Cost Center"
                        subtitle="Select the cost/job center to close stock for"
                        action={
                            <button
                                onClick={handleFullReset}
                                disabled={isBusy}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                            >
                                <RotateCcw className="h-3.5 w-3.5" /> Reset
                            </button>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <Label required>Cost / Job Center</Label>
                                <div className="relative">
                                    <select
                                        className={`${inputCls} appearance-none pr-10`}
                                        value={costCenter}
                                        onChange={(e) => setCostCenter(e.target.value)}
                                        disabled={ccLoading || isBusy}
                                    >
                                        <option value="">{ccLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {costCenters.map((c) => (
                                            <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Code} — {c.CC_Name}</option>
                                        ))}
                                    </select>
                                    <SelectIcon loading={ccLoading} />
                                </div>
                            </div>

                            {showFields && (
                                <>
                                    <div>
                                        <Label>{fromDateLabel}</Label>
                                        <input type="text" readOnly value={fromDateDisplay || ''} className={inputCls} />
                                    </div>
                                    <div>
                                        <Label required>To Date</Label>
                                        <CustomDatePicker
                                            value={toDate}
                                            onChange={setToDate}
                                            disabled={isBusy || lastCloseLoading}
                                            minDate={parseAPIDate(effectiveFromDate)}
                                            maxDate={new Date()}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Section 2: Stock Values ────────────────────────────── */}
                {showFields && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader
                            icon={IndianRupee}
                            title="Stock Values"
                            subtitle="Computed for the selected period"
                        />
                        <div className="p-6 md:p-8">
                            {stockAmountLoading ? (
                                <div className="flex items-center justify-center py-8 text-gray-400">
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                                </div>
                            ) : showStockInfo ? (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                        <div>
                                            <Label>Stock Amount</Label>
                                            <input type="text" readOnly value={formatIndianCurrency(currentStockAmount)} className={`${inputCls} font-semibold`} />
                                        </div>
                                        <div>
                                            <Label>Last Closing Amount</Label>
                                            <input type="text" readOnly value={formatIndianCurrency(lastClosingAmount)} className={`${inputCls} font-semibold`} />
                                        </div>
                                        <div>
                                            <Label>Current Value</Label>
                                            <input type="text" readOnly value={formatIndianCurrency(currentValue)} className={`${inputCls} font-bold`} />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        {[
                                            { label: 'Total Purchase Amount', value: stockAmount?.TotalPurchaseAmt },
                                            { label: 'New Stock Amount', value: stockAmount?.NewStockAmt },
                                            { label: 'Old Stock Amount', value: stockAmount?.OldStockAmt },
                                            { label: 'Daily Issue', value: stockAmount?.DailyIssueAmt },
                                        ].map(({ label, value }) => (
                                            <div key={label} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                                <p className="font-semibold text-gray-800 dark:text-gray-100">₹ {formatIndianCurrency(value || 0)}</p>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="px-4 py-3 rounded-xl text-sm font-medium bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 text-center">
                                    Select the To Date above to compute stock values
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Section 3: Closing Details ────────────────────────── */}
                {showStockInfo && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader
                            icon={Boxes}
                            title="Closing Details"
                            subtitle="Adjustment type, amount and remarks"
                        />
                        <div className="p-6 md:p-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div>
                                    <Label required>Transaction Type</Label>
                                    <div className="relative">
                                        <select
                                            className={`${inputCls} appearance-none pr-10`}
                                            value={transactionType}
                                            onChange={(e) => setTransactionType(e.target.value)}
                                            disabled={isBusy}
                                        >
                                            <option value="">-Please Select-</option>
                                            {TRANSACTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        <SelectIcon loading={false} />
                                    </div>
                                </div>
                                <div>
                                    <Label required>Amount (₹)</Label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0.00"
                                        className={inputCls}
                                        value={amount}
                                        onChange={handleAmountChange}
                                        disabled={isBusy}
                                    />
                                </div>
                                <div>
                                    <Label>Closing Amount (₹)</Label>
                                    <input
                                        type="text"
                                        readOnly
                                        value={closingAmount !== null ? formatIndianCurrency(closingAmount) : ''}
                                        className={`${inputCls} font-bold`}
                                    />
                                </div>
                            </div>

                            <Label required>User Remarks</Label>
                            <textarea
                                rows={3}
                                placeholder="Enter remarks…"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                disabled={isBusy}
                                className={`${inputCls} resize-none`}
                            />
                        </div>
                    </div>
                )}

                {/* ── Summary ───────────────────────────────────────────── */}
                {showSummary && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader icon={CheckCircle} title="Stock Close Summary" subtitle="Review before submitting" />
                        <div className="p-6 md:p-8">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                {[
                                    { label: 'Cost Center', value: `${costCenter} — ${selectedCCName}` },
                                    { label: fromDateLabel, value: fromDateDisplay },
                                    { label: 'To Date', value: formatDateForAPI(toDate) },
                                    { label: 'Transaction Type', value: transactionType },
                                    { label: 'Amount', value: `₹ ${formatIndianCurrency(numAmount)}` },
                                    { label: 'Current Value', value: `₹ ${formatIndianCurrency(currentValue)}` },
                                    { label: 'Closing Amount', value: `₹ ${formatIndianCurrency(closingAmount || 0)}` },
                                    { label: 'Created By', value: userName },
                                    { label: 'Remarks', value: remarks, wide: true },
                                ].map(({ label, value, wide }) => (
                                    <div key={label} className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 ${wide ? 'md:col-span-3' : ''}`}>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                        <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value ?? '—'}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Action Buttons ────────────────────────────────────── */}
                {showFields && (
                    <div className="flex items-center justify-end gap-3 pb-6">
                        <button
                            onClick={handleFullReset}
                            disabled={isBusy}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                        >
                            <RotateCcw className="h-4 w-4" /> Reset
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {saveLoading
                                ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                                : <><Send className="h-4 w-4" /> Submit</>}
                        </button>
                    </div>
                )}

            </div>
        </div>
    );
};

export default CCStockClose;
